import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ErrorIcon } from 'assets/icons/toast-error.svg';
import ValuesModal from 'components/ValuesModal/ValuesModal';
import errorMessagesFormatted from 'utils/formattedErrorText';

import type { IErrorMessages } from 'store/slices/dataSets/interfaces/IErrorItem';
import type IErrorItem from '../interfaces/IErrorItem';

import styles from '../scss/ErrorItem.module.scss';

function ErrorItem({
  error,
}: IErrorItem) {
  const { t } = useTranslation();
  const { rowNumber, items, errorMessages } = error || {};
  const [activeModalId, setActiveModalId] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleShowMoreClick = (modalId: string) => {
    setActiveModalId((prevId) => (prevId === modalId ? null : modalId));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setActiveModalId(null);
    }
  };

  useEffect(() => {
    if (activeModalId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeModalId]);

  return (
    <div className={styles.root}>
      <div className={styles.errorItemList}>
        <div className={styles.errorItemListTitle}>
          <span>{t('dataSetsManagement.importErrorsModal.validationErrors')}</span>
        </div>
        <div className={styles.rowWrapper}>
          <span>
            <b>{t('dataSetsManagement.importErrorsModal.errorRow')}</b>
            {' '}
            {rowNumber}
          </span>
        </div>
        {items.map((item: string, index: number) => (
          <div
            key={[item, index].join('_')}
            className={styles.errorItemListContent}
          >
            <div className={styles.errorLeftWrapper}>
              <span>
                <b>{t('dataSetsManagement.importErrorsModal.errorColumn')}</b>
                {' '}
                {item}
              </span>
            </div>
            <div className={styles.errorRightWrapper}>
              <ErrorIcon />
              <div className={styles.errorList}>
                {Object.values(errorMessages[index] || {}).flat().map((
                  errorMessage: IErrorMessages,
                  errorIndex: number,
                ) => {
                  const modalId = `${item}-${index}-${errorIndex}`;
                  return (
                    <span key={errorMessage.error} className={styles.errorItem}>
                      {errorMessagesFormatted(errorMessage.error, errorMessage.params)}
                      {errorMessage.params.values ? (
                        <div ref={activeModalId === modalId ? modalRef : null}>
                          <ValuesModal
                            isVisible={activeModalId === modalId}
                            values={errorMessage.params.values}
                            onClick={() => handleShowMoreClick(modalId)}
                          />
                        </div>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ErrorItem;
